import {FC, useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {runInAction, toJS} from 'mobx';
import {observer} from 'mobx-react-lite';

import AwesomeDebouncePromise from 'awesome-debounce-promise';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';

import RootStore from '../../Core/mobx/RootStore';
import {AgentStore} from '../AgentStore';

import CreateCustomerPortalPanel from './CreateCustomer/CreateCustomerPortalPanel';

import {usePrevious} from '../../Core/hooks/usePrevious';
import {CustomerPortal} from '../types';

type CustomerItemProps = {
    contact: CustomerPortal;
    selectedCustomer: CustomerPortal;
    onContactSelect: (customer: CustomerPortal) => void;
};

const CustomerItem: FC<CustomerItemProps> = ({contact, selectedCustomer, onContactSelect}: CustomerItemProps) => (
    <TableRow>
        <TableCell>{contact.lastName}</TableCell>
        <TableCell>{contact.firstName}</TableCell>
        <TableCell>{contact.email}</TableCell>
        <TableCell>{contact.phoneNumber}</TableCell>
        <TableCell>
            <Button
                variant="contained"
                color="primary"
                itemID={contact.id}
                disabled={selectedCustomer?.id === contact.id}
                onClick={() => onContactSelect(contact)}
            >
                Select
            </Button>
        </TableCell>
    </TableRow>
);

const CustomerPortalPanel = observer(({store}: {store: AgentStore}) => {
    const customerPortalStore = store.customerPortalStore;
    const selectedCustomer = customerPortalStore.selectedCustomer;

    const customers = customerPortalStore.customers;
    const search = customerPortalStore.search;

    const [openCreateCustomer, setOpenCreateCustomer] = useState(false);

    const history = useHistory();
    const prevSearch = usePrevious(search);

    const getContacts = async (search?: string) => {
        customerPortalStore.getCustomers(search || '');
    };

    const searchTextChanged = async (search: string) => {
        runInAction(() => {
            customerPortalStore.search = search;
        });
    };

    const doSearch = useCallback(
        AwesomeDebouncePromise(async (search: string) => {
            await getContacts(search);
        }, 600),
        [],
    );

    const onContactSelect = useCallback(
        (contact) => {
            customerPortalStore.setSelectedCustomer(toJS(contact));
        },
        [customerPortalStore],
    );

    useEffect(() => {
        if (prevSearch !== search || search === '') {
            doSearch(search);
        }
    }, [search, doSearch, prevSearch]);

    return (
        <div style={{width: '100%'}}>
            <Grid container spacing={3} style={{width: '100%'}}>
                <Grid item xs={6}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setOpenCreateCustomer(true);
                        }}
                    >
                        Create Customer
                    </Button>

                    {false && selectedCustomer == null ? (
                        <Button
                            style={{marginLeft: 15}}
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                RootStore.agentPortalStore.reset();
                                history.push('/agent/newform');
                            }}
                        >
                            New Customer
                        </Button>
                    ) : null}

                    <CreateCustomerPortalPanel
                        open={openCreateCustomer}
                        close={() => {
                            setOpenCreateCustomer(false);
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        id="standard-basic"
                        label="Search Contacts"
                        style={{width: '100%'}}
                        value={search}
                        onChange={(e) => {
                            searchTextChanged(e.target.value);
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    {customers?.length ? (
                        <TableContainer component={Paper}>
                            <Table style={{minWidth: '100%'}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Last Name</TableCell>
                                        <TableCell>First Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        {/* <TableCell>Lead Create Date</TableCell> */}
                                        <TableCell>Control</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {customers?.map((contact) => (
                                        <CustomerItem
                                            key={contact.id}
                                            contact={contact}
                                            selectedCustomer={selectedCustomer!}
                                            onContactSelect={onContactSelect}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Grid container spacing={3} style={{marginTop: 30, width: '100%'}}>
                            <Grid item xs={12} style={{textAlign: 'center'}}>
                                {customers === null && search ? (
                                    <div>Please wait... loading...</div>
                                ) : (
                                    <div>No contact found</div>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </div>
    );
});

export default CustomerPortalPanel;
